import { useCallback } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { setIncompleteFormsAction } from 'domain/env/actions';
import { envIncompleteFormsSelector } from 'domain/env/selectors';
import { hasSkippedRequiredForms, saveFirstIntendedUrl } from 'libs/requiredFormsStorage';
import { TFormAssignedTo } from 'pages/Forms/types';

export interface IIncompleteRequiredForm {
  title: string;
  assigned_to_type: TFormAssignedTo;
  form_id: number;
  unit_id: number | null;
}

const matchesIncompleteForm = (
  form: IIncompleteRequiredForm,
  formId: number,
  unitId?: number | null
) => form.form_id === formId && (form.unit_id == null || form.unit_id === unitId);

const useIncompleteForms = () => {
  const dispatch = useDispatch();
  const forms: IIncompleteRequiredForm[] = useMappedState(envIncompleteFormsSelector) ?? [];

  const isIncompleteRequiredForm = useCallback(
    (formId: number, unitId?: number | null) =>
      forms.some((f) => matchesIncompleteForm(f, formId, unitId)),
    [forms]
  );

  const removeIncompleteForm = useCallback(
    (formId: number, unitId?: number | null): IIncompleteRequiredForm[] => {
      const remaining = forms.filter((f) => !matchesIncompleteForm(f, formId, unitId));
      dispatch(setIncompleteFormsAction({ forms: remaining }));
      return remaining;
    },
    [forms, dispatch]
  );

  return { forms, isIncompleteRequiredForm, removeIncompleteForm };
};

export const handleIncompleteFormRedirect = (
  forms: IIncompleteRequiredForm[] | undefined,
  currentPath: string
): string | null => {
  if (!Array.isArray(forms) || forms.length === 0) return null;
  if (hasSkippedRequiredForms()) return null;

  const { form_id, unit_id } = forms[0];
  const params = new URLSearchParams();
  params.set('view', 'submission');
  if (unit_id) params.set('unit_id', String(unit_id));

  const redirectPath = `/forms/${form_id}?${params.toString()}`;

  if (currentPath !== redirectPath) {
    saveFirstIntendedUrl(currentPath);
    return redirectPath;
  }

  return null;
};

export default useIncompleteForms;
